import { get, post, put, del } from './client';
import type {
  Merchant,
  MerchantListResponse,
  ListMerchantsParams,
  CreateMerchantParams,
  UpdateMerchantParams,
} from '@/types/merchant';

/**
 * List merchants with optional status filter and pagination.
 * Platform admin sees all, merchant users see only their own (backend filters).
 */
export async function listMerchants(params?: ListMerchantsParams): Promise<MerchantListResponse> {
  const queryParams: Record<string, string> = {};
  if (params?.status && params.status !== 'all') queryParams.status = params.status;
  if (params?.limit) queryParams.limit = String(params.limit);
  if (params?.cursor) queryParams.cursor = params.cursor;
  return get<MerchantListResponse>('/admin/merchants', queryParams);
}

/**
 * Get a single merchant by ID.
 * Returns the merchant object directly (not wrapped in { data }).
 */
export async function getMerchant(merchantId: string): Promise<Merchant> {
  return get<Merchant>(`/admin/merchants/${encodeURIComponent(merchantId)}`);
}

/**
 * Create a new merchant (platform_admin only).
 */
export async function createMerchant(params: CreateMerchantParams): Promise<Merchant> {
  return post<Merchant>('/admin/merchants', params);
}

/**
 * Update an existing merchant (platform_admin only).
 * Also used for reactivation via { status: 'active' }.
 */
export async function updateMerchant(merchantId: string, params: UpdateMerchantParams): Promise<Merchant> {
  return put<Merchant>(`/admin/merchants/${encodeURIComponent(merchantId)}`, params);
}

/**
 * Soft-delete a merchant (platform_admin only).
 * Sets status to 'inactive'. Returns 409 if merchant has linked services.
 */
export async function deleteMerchant(merchantId: string): Promise<Merchant> {
  return del<Merchant>(`/admin/merchants/${encodeURIComponent(merchantId)}`);
}
